import { z } from "zod";
import { redactSensitiveText } from "./runs";
import { zotProposalSchema, type ZotProposal } from "./zot";

/**
 * The kinds of documents Zot produces in the Artifacts panel, as the tabs label them:
 * handoff documents, change sets, drafts, reports, diagrams, and exports.
 */
export const zotArtifactKinds = [
  "handoff",
  "changeset",
  "draft",
  "report",
  "diagram",
  "export",
] as const;
export const zotArtifactKindSchema = z.enum(zotArtifactKinds);
export type ZotArtifactKind = z.infer<typeof zotArtifactKindSchema>;

/**
 * One footer action on an artifact. Pressing it appends the proposal to the chat as a new
 * assistant message, so it passes through the same confirm and spend gate as any other
 * proposal. Nothing changes state before the caller confirms.
 */
export const zotArtifactActionSchema = z.object({
  label: z.string().trim().min(1).max(80),
  proposal: zotProposalSchema,
});
export type ZotArtifactAction = z.infer<typeof zotArtifactActionSchema>;

/**
 * One versioned project artefact linked to the chat that made it. Content is redacted
 * before it is stored or shown, so titles, subtitles, and bodies never carry a secret.
 * Versions share an `artifactKey` within a thread; the version number starts at 1.
 */
export const zotArtifactSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  threadId: z.uuid(),
  artifactKey: z.string().min(1).max(120),
  version: z.number().int().min(1),
  kind: zotArtifactKindSchema,
  title: z.string().min(1).max(200),
  subtitle: z.string().max(300),
  bodyMarkdown: z.string().min(1).max(100_000),
  actions: z.array(zotArtifactActionSchema).max(6),
  createdAt: z.iso.datetime(),
});
export type ZotArtifact = z.infer<typeof zotArtifactSchema>;

/** `GET .../artifacts`: the latest version of each artifact key in a thread, oldest first. */
export const zotArtifactListSchema = z.array(zotArtifactSchema);
export type ZotArtifactList = z.infer<typeof zotArtifactListSchema>;

/** `GET .../artifacts/:artifactId`: one version plus every version number kept for its key. */
export const zotArtifactDetailSchema = z.object({
  artifact: zotArtifactSchema,
  versions: z.array(z.number().int().min(1)),
});
export type ZotArtifactDetail = z.infer<typeof zotArtifactDetailSchema>;

/**
 * `POST .../threads/:threadId/artifacts`: a new version under the given key, or under a
 * fresh key when none is given. The server redacts secrets before storing.
 */
export const createZotArtifactSchema = z.object({
  artifactKey: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9-]+$/, "Keys read lower-case with hyphens, for example handoff-sign-in.")
    .optional(),
  kind: zotArtifactKindSchema,
  title: z.string().trim().min(1).max(200),
  subtitle: z.string().trim().max(300).optional().default(""),
  bodyMarkdown: z.string().trim().min(1).max(100_000),
  actions: z.array(zotArtifactActionSchema).max(6).optional().default([]),
});
export type CreateZotArtifact = z.infer<typeof createZotArtifactSchema>;

/**
 * `POST .../artifacts/:artifactId/proposals`: appends the named footer action as a proposal
 * message in the artifact's thread. The index must be within the artifact's actions.
 */
export const createZotArtifactProposalSchema = z.object({
  actionIndex: z.number().int().min(0).max(5),
});
export type CreateZotArtifactProposal = z.infer<typeof createZotArtifactProposalSchema>;

/** Redacts secret-like values from artifact input before it is stored or shown. */
export function redactZotArtifactInput<
  T extends {
    title: string;
    subtitle: string;
    bodyMarkdown: string;
    actions?: ZotArtifactAction[];
  },
>(input: T): T {
  return {
    ...input,
    title: redactSensitiveText(input.title),
    subtitle: redactSensitiveText(input.subtitle),
    bodyMarkdown: redactSensitiveText(input.bodyMarkdown),
    actions: (input.actions ?? []).map((action) => ({
      label: redactSensitiveText(action.label),
      proposal: redactZotProposal(action.proposal),
    })),
  };
}

/**
 * Field names that always carry a credential. A value under one of these is replaced
 * whole, because a bare secret such as `hunter2` carries no shape for the text redactor
 * to recognise.
 */
const credentialFieldNames = [
  "apikey",
  "secret",
  "token",
  "password",
  "passphrase",
  "credential",
  "authorization",
  "cookie",
  "privatekey",
];

/** True when a field name names a credential, whatever the nesting. */
function isCredentialField(key: string): boolean {
  const normalised = key
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\s\p{Default_Ignorable_Code_Point}_-]/gu, "");
  return credentialFieldNames.some((name) => normalised.includes(name));
}

/**
 * Redacts secret-like values from any proposal payload, at every depth. Nested objects
 * and arrays are walked, so a secret cannot hide one level down. Keys survive, because
 * the confirm gate and the audit trail read them; only the values change.
 */
export function redactZotProposalValue(value: unknown): unknown {
  if (typeof value === "string") return redactSensitiveText(value);
  if (Array.isArray(value)) return value.map((item) => redactZotProposalValue(item));
  if (!value || typeof value !== "object") return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (isCredentialField(key)) {
      out[key] = "[redacted]";
      continue;
    }
    out[key] = redactZotProposalValue(child);
  }
  return out;
}

/**
 * Redacts free text inside one artifact footer-action proposal. Structure, kinds, and
 * identifiers stay, because the confirm gate and audit trail need them; secret-like
 * values in labels, summaries, and prompts do not, so they are stripped.
 */
export function redactZotProposal(proposal: ZotProposal): ZotProposal {
  return {
    summary: redactSensitiveText(proposal.summary),
    changes: proposal.changes.map((change) => redactSensitiveText(change)),
    action: {
      ...proposal.action,
      input: redactZotProposalValue(proposal.action.input),
    } as ZotProposal["action"],
    ...(proposal.binding
      ? {
          binding: {
            ...proposal.binding,
            observed: redactSensitiveText(proposal.binding.observed),
          },
        }
      : {}),
    ...(proposal.expiresAt ? { expiresAt: proposal.expiresAt } : {}),
  };
}
